import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { Box } from "@mui/material";
import {
  createItem,
  fetchItem,
  fetchRepositories
} from "../../../../redux/actions/repositoryActions";
import {
  enqueueSuccessSnackbar,
  enqueueErrorSnackbar
} from "../../../../redux/actions/snackbarActions";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import AddNewContentForm from "../../../../components/Repository/AddNewContentForm";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import ProcessingModal from "../../../../components/ProcessingModal/ProcessingModal";
import {
  buildManageRespositoryRoute,
  buildManageRepositoryItemRoute
} from "../../../../routes/AdminRoutes";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { ActionCableConsumer } from "react-actioncable-provider";
import { useTranslation } from "react-i18next";
import { showJobWarnings } from "../../../../utils/repositoryUtils";
import { useRepositoryQuiz } from "../../../../hooks/repositoryItems";

const selector = makeGetEntities();

const AddRepositoryItem = ({
  match: {
    params: { repositoryId },
    url
  }
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { repository, userId, loading } = useSelector(
    ({ repositoryState, userState }) => ({
      repository: selector(repositoryState, {
        type: "libraries",
        id: repositoryId
      }),
      loading: repositoryState.loading,
      userId: userState.user && userState.user.id
    })
  );

  const [submitAndNew, setSubmitAndNew] = useState(false);
  const [contentFormKey, setContentFormKey] = useState(0);
  const [itemId, setItemId] = useState(null);
  const [runningJobId, setRunningJobId] = useState(null);
  const [isBeingCreated, setIsBeingCreated] = useState(false);

  const { getQuiz, createQuiz, importQuizQuestions } = useRepositoryQuiz();

  const handleCloseIconClick = () => {
    dispatch(push(buildManageRespositoryRoute(repositoryId)));
  };

  /**
   * If the user creates a new item, the new repository item ID will be passed to
   * the component. If the user pressed the Submit & New button, the new repository item
   * data will be reset and a new key will be generated for the form so it will be reset.
   * Otherwise, if the user pressed submit, they will be directed to the manage repository
   * item page for the newly added item
   */
  const onCreateSuccess = newItemId => {
    if (!submitAndNew) {
      dispatch(push(buildManageRepositoryItemRoute(repositoryId, newItemId)));
    } else {
      dispatch(
        enqueueSuccessSnackbar({
          message: t("addNewContent.uploadProcessingSuccess")
        })
      );
      setContentFormKey(contentFormKey + 1);
      setSubmitAndNew(false);
      setIsBeingCreated(false);
    }
  };

  const createQuizItem = async (newRepositoryItemData, endCallback) => {
    let response = await createQuiz({
      ...newRepositoryItemData,
      creatorId: userId,
      libraryId: repository.contextId
    });
    if (!response.errors) {
      response = await importQuizQuestions(newRepositoryItemData.file, true);
    }
    if (response.errors) {
      console.error(response.errors);
      setIsBeingCreated(false);
      dispatch(
        enqueueErrorSnackbar({
          message: t("addNewContent.quizCreationError")
        })
      );
      endCallback();
    } else {
      if (response.meta && response.meta.errors) {
        /*Some questions were imported, some weren't*/
        response.meta.errors.forEach(error =>
          dispatch(
            enqueueErrorSnackbar({
              message: error.detail
            })
          )
        );
      }
      const quiz = getQuiz();
      setItemId(quiz.item.id);
      onCreateSuccess(quiz.item.id);
    }
  };

  const handleSubmit = (newRepositoryItemData, endCallback) => {
    setIsBeingCreated(true);
    if (newRepositoryItemData.isQuizImport) {
      return createQuizItem(newRepositoryItemData, endCallback);
    }
    dispatch(
      createItem(
        {
          ...newRepositoryItemData,
          creatorId: userId,
          libraryId: repository.contextId
        },
        response => {
          setItemId(response.data.id);
          if (!!response.data.attributes.jobId) {
            setRunningJobId(response.data.attributes.jobId);
          } else {
            onCreateSuccess(response.data.id);
          }
        },
        error => {
          console.error(error);
          setIsBeingCreated(false);
          endCallback();
        }
      )
    );
  };

  const handleSubmitAndNew = newRepositoryItemData =>
    setSubmitAndNew(newRepositoryItemData);

  useEffect(() => {
    if (!!submitAndNew) {
      handleSubmit(submitAndNew);
    }
  }, [submitAndNew]);

  useEffect(() => {
    if (!repository) {
      dispatch(fetchRepositories(repositoryId));
    }
  }, []);

  return repository ? (
    <AdminPageLayout
      title={repository.title}
      onCloseIconClick={handleCloseIconClick}
    >
      <Box mt={4}>
        <ProcessingModal
          open={isBeingCreated}
          messageText={t("addNewContent.uploadProcessingMessage")}
        />
        {!!runningJobId && (
          <ActionCableConsumer
            channel={{
              channel: "ScormProgressChannel",
              job_id: runningJobId
            }}
            onReceived={response => {
              dispatch(fetchItem(itemId));
              setRunningJobId(null);
              showJobWarnings(response);
              onCreateSuccess(itemId);
            }}
          />
        )}
        <AddNewContentForm
          key={contentFormKey}
          onSubmit={handleSubmit}
          onSubmitAndNew={handleSubmitAndNew}
          buttonTypes={["submit", "submitAndNew"]}
        />
      </Box>
    </AdminPageLayout>
  ) : loading ? (
    <LoadingSpinner />
  ) : null;
};

export default AddRepositoryItem;

AddRepositoryItem.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The repository ID for the new item
       */
      repositoryId: PropTypes.string
    })
  })
};
